import { Loader2 } from './icons.tsx'
import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from './cx.ts'
import { ActionMenu } from './menu.tsx'

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  // A padding class passed by the caller replaces the default: with Tailwind v4
  // both would be generated and CSS order (not class order) would decide.
  const padded = !/(^|\s)p-\d/.test(className ?? '')
  return (
    <div
      className={cx('rounded-card bg-surface shadow-card', padded && 'p-5', className)}
      {...rest}
    />
  )
}

/** An area of the app, each with its own colour (tokens in `styles.css`). */
export type Area = 'sell' | 'people' | 'stock' | 'buy' | 'insight' | 'kitchen' | 'neutral' | 'take'

// Spelled out so Tailwind sees every class.
const AREA_TILE: Record<Area, string> = {
  sell: 'bg-sell-light text-sell',
  people: 'bg-people-light text-people',
  stock: 'bg-stock-light text-stock',
  buy: 'bg-buy-light text-buy',
  insight: 'bg-insight-light text-insight',
  kitchen: 'bg-kitchen-light text-kitchen',
  neutral: 'bg-neutral-light text-neutral',
  take: 'bg-take-light text-take',
}
const AREA_WASH: Record<Area, string> = {
  sell: 'from-sell-wash',
  people: 'from-people-wash',
  stock: 'from-stock-wash',
  buy: 'from-buy-wash',
  insight: 'from-insight-wash',
  kitchen: 'from-kitchen-wash',
  neutral: 'from-neutral-wash',
  take: 'from-take-wash',
}
const TILE_SIZE = {
  sm: 'size-7 rounded-lg',
  md: 'size-9 rounded-xl',
  lg: 'size-11 rounded-xl',
} as const

/** An icon on a soft square of its area's colour. */
export function IconTile({
  area = 'sell',
  size = 'md',
  className,
  children,
}: {
  area?: Area
  size?: keyof typeof TILE_SIZE
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cx(
        'flex shrink-0 items-center justify-center',
        TILE_SIZE[size],
        AREA_TILE[area],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
  secondary,
  more,
}: {
  title: ReactNode
  subtitle?: ReactNode
  /** The everyday buttons, always visible. */
  actions?: ReactNode
  /** Less used buttons: in a row on wide screens, behind "More" on phones. */
  secondary?: ReactNode
  /** Rare or risky actions: behind "More" on every screen. */
  more?: ReactNode
}) {
  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        {/* The title gives way before the buttons drop to a line of their own. */}
        <h1 className="min-w-0 flex-1 basis-36 text-page-title text-ink">{title}</h1>
        {(actions || secondary || more) && (
          // max-w-full: on a narrow phone the buttons wrap instead of running off the edge.
          <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">
            {secondary && <ActionMenu>{secondary}</ActionMenu>}
            {actions}
            {more && <ActionMenu collapse="always">{more}</ActionMenu>}
          </div>
        )}
      </div>
      {/* Under the whole row, so buttons beside the title never squeeze it. */}
      {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
    </div>
  )
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      {icon && <div className="mb-3 rounded-full bg-primary-light p-3 text-primary">{icon}</div>}
      <p className="font-semibold text-ink">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

const TONES: Record<Tone, string> = {
  neutral: 'bg-soft text-ink',
  primary: 'bg-primary-light text-primary',
  success: 'bg-success-light text-success',
  warning: 'bg-warning-light text-warning',
  danger: 'bg-danger-light text-danger',
  info: 'bg-info-light text-info',
}

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-10 text-sm text-muted">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span>{label}…</span>
    </div>
  )
}

export function Stat({
  label,
  value,
  hint,
  icon,
  area,
  className,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  icon?: ReactNode
  /** Tints the card and its icon with the colour of the part of the app the number is about. */
  area?: Area
  className?: string
}) {
  return (
    <Card
      className={cx(
        'flex items-start justify-between gap-3 p-4 sm:p-5',
        area && cx('bg-linear-160 to-surface to-70%', AREA_WASH[area]),
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-sm text-muted">{label}</p>
        <p className="tabular mt-1 text-xl font-bold break-words text-ink sm:text-2xl">
          {value ?? <Skeleton className="mt-2 h-6 w-28" />}
        </p>
        {hint && <p className="mt-1 text-xs text-subtle">{hint}</p>}
      </div>
      {icon && (
        <IconTile area={area ?? 'sell'} size="lg" className="hidden sm:flex">
          {icon}
        </IconTile>
      )}
    </Card>
  )
}

export function Banner({
  tone = 'warning',
  children,
  action,
}: {
  tone?: Tone
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div
      className={cx(
        'flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm',
        TONES[tone],
      )}
    >
      <div>{children}</div>
      {action}
    </div>
  )
}

/** A grey placeholder shaped like the content that is still loading. */
export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden className={cx('block animate-pulse rounded-lg bg-soft', className)} />
}
